import {
  APIComponentAttributes,
  APIFieldType,
  APIReferenceAttributes,
  APIReferenceType,
  ArrowType,
} from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { fieldInterface } from '@ardoq/field-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { modelOperations } from '@ardoq/core';
import {
  GetCssClassNames,
  GetCssColors,
  GetFields,
  GetGlobalReferenceType,
  GetGlobalTypeId,
  GetLineBeginning,
  GetLineEnding,
  GetModelType,
  Get,
  GetReferencesCount,
  GetReferencesForComponent,
  GetReferencesInWorkspace,
  GetReferenceTypeName,
  GetStubReferencesWithTypeName,
  GetStyle,
  GetTypeNameFilter,
  HasURLFields,
  IsExternallyManaged,
  IsIncludedInContextByFilter,
  IsReference,
  GetRawLabel,
  GetReferenceTypeNameByReferenceId,
  ReferenceInterfaceImplementation,
  GetGraphSnapshot,
  GetReferencesInRootWorkspace,
} from '@ardoq/reference-interface';
import { isNil, pick } from 'lodash';
import { globalFilters as Filters } from 'collections/filters';
import { filterCache } from 'collections/filterCache';
import { filterInterface } from '@ardoq/filter-interface';
import { APIEntityType, ArdoqId, LinkedComponent } from '@ardoq/api-types';
import {
  getFillAndStroke,
  ensureContrast,
  getSanitizedRgbaClassName,
} from '@ardoq/color-helpers';
import {
  getEmptyComponentGraphData,
  GetCssClassNamesOption,
  ReferenceLabelSource,
  SimpleReferenceModel,
} from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
import { getDisplayLabel as computeDisplayLabel } from 'modelInterface/util';
import { referenceOperations } from '@ardoq/reference-operations';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { models$ } from 'streams/models/models$';
import { getAddToMaps } from '@ardoq/common-helpers';
import { references$ } from 'streams/references/references$';
import { getCurrentGraphModel } from 'modelInterface/graphModel$';

// NOTE:
// The purpose of BB interfaces is to avoid exposing the BB model. In practice
// this means you should __never__ return a BB model from these functions.
const get: Get = referenceId => {
  return references$.state.byId[referenceId] ?? null;
};

const getGlobalTypeId: GetGlobalTypeId = referenceId => {
  const data = get(referenceId);
  if (!data || isNil(data.type)) return null;
  return referenceOperations.getGlobalTypeId(data);
};

const resolveComponentFilterData = (
  component: APIComponentAttributes,
  ctx?: ReferenceFilterContext
) => ({
  attributes: component,
  fields: fieldInterface.getFieldsByComponentType(
    component.typeId,
    component.model
  ),
  typeName:
    ctx?.componentTypeByComponentId[component._id]?.name ??
    componentInterface.getTypeName(component._id) ??
    '',
  incomingRefCount: componentInterface.getSources(component._id).length,
  outgoingRefCount: componentInterface.getTargets(component._id).length,
});

const isIncludedInContextByFilter: IsIncludedInContextByFilter = (
  reference,
  ctx,
  scopeSource
) => {
  const cached = Filters.getCachedFilterResult(reference._id);
  if (cached !== undefined) return cached;
  const sourceComponent = componentInterface.get(reference.source);
  const targetComponent = componentInterface.get(reference.target);
  if (!sourceComponent || !targetComponent) return true;
  const source = resolveComponentFilterData(sourceComponent, ctx);
  const target = resolveComponentFilterData(targetComponent, ctx);
  return Filters.isReferenceIncludedInContextByFilter(
    scopeSource ?? null,
    {
      attributes: reference,
      fields: fieldInterface.getFieldsByReferenceType(
        reference.type,
        reference.model
      ),
      typeName:
        ctx?.referenceTypeByReferenceId[reference._id]?.name ??
        getReferenceTypeName(reference) ??
        '',
    },
    source,
    target,
    getCurrentGraphModel()
  );
};

const getDisplayLabel: GetRawLabel = referenceId => {
  const { value, includeTime } = filterInterface.getReferenceLabelFilter();

  if (value === ReferenceLabelSource.NONE) {
    return null;
  }

  const reference = get(referenceId);
  if (!reference) {
    return null;
  }

  const field = fieldInterface.getGlobalFieldByName(value);

  const referenceTypes = workspaceInterface.getReferenceTypes(
    reference.rootWorkspace
  );

  return computeDisplayLabel(
    reference,
    field,
    modelOperations.getModelType(referenceTypes, reference.type),
    value,
    !includeTime
  );
};

const isReference: IsReference = referenceId => get(referenceId) !== null;

const hasURLFields: HasURLFields = referenceId => {
  const data = get(referenceId);
  if (!data) return false;
  const urlFields = fieldInterface
    .getFieldsByReferenceType(data.type, data.model)
    .filter(field => field.type === APIFieldType.URL);
  return referenceOperations.hasURLFields(data, urlFields);
};

const getCssClassNames: GetCssClassNames = referenceId => {
  const reference = get(referenceId);
  if (!reference) {
    return null;
  }
  let filterColor = filterCache.getColor(reference._id);
  if (filterColor) {
    if (filterColor.startsWith('#'))
      filterColor = filterColor.replace('#', 'filter');
    // Provide valid CSS class name
    if (filterColor.toLowerCase().startsWith('rgba'))
      filterColor = getSanitizedRgbaClassName(filterColor);
  }

  return referenceOperations.getCssClassNames(reference, filterColor);
};

const getReferenceCssColors = (
  referenceId: ArdoqId,
  options: GetCssClassNamesOption
) => {
  const referenceType = getModelType(referenceId);
  if (!referenceType) {
    return { fill: undefined, stroke: undefined };
  }
  const { color } = referenceType;
  // Get reference type color, respecting the useAsBackgroundStyle option
  return getFillAndStroke(color, options.useAsBackgroundStyle);
};

const getCssColors: GetCssColors = (referenceId, options) => {
  const data = get(referenceId);
  const filterColor = data && filterCache.getColor(referenceId);
  if (filterColor) {
    const { useAsBackgroundStyle, isNewConditionalFormatting } = options;
    const isLightenedFill = useAsBackgroundStyle && !isNewConditionalFormatting;
    const { fill, stroke: componentStroke } = getFillAndStroke(
      filterColor,
      isLightenedFill
    );

    // Ensure stroke has contrast with fill when using conditional formatting colors
    const stroke =
      isNewConditionalFormatting && fill && componentStroke
        ? ensureContrast(fill, componentStroke)
        : componentStroke;

    return { fill, stroke };
  }
  return getReferenceCssColors(referenceId, options);
};

const getReferencesForComponent: GetReferencesForComponent = componentId =>
  getAllReferences().filter(ref =>
    referenceOperations.isForComponent(ref, componentId)
  );

const getReferencesInWorkspace: GetReferencesInWorkspace = workspaceId =>
  getAllReferences().filter(ref =>
    referenceOperations.isInWorkspace(ref, workspaceId)
  );

const getReferencesInRootWorkspace: GetReferencesInRootWorkspace =
  workspaceId =>
    getAllReferences().filter(ref => ref.rootWorkspace === workspaceId);

// Virtual (collapsed-path) references resolve their reference type from the
// synthetic virtual model in the models collection (keyed by reference.model =
// VIRTUAL_MODEL_ID); their type lives there, not on any workspace model.
const getVirtualReferenceType = (
  type: APIReferenceAttributes['type']
): APIReferenceType | null =>
  models$.state.byId[VIRTUAL_MODEL_ID]?.referenceTypes[type] ?? null;

const resolveModelType = (
  data: APIReferenceAttributes
): APIReferenceType | null => {
  if (data.model === VIRTUAL_MODEL_ID) {
    return getVirtualReferenceType(data.type);
  }
  return modelOperations.getModelType(
    workspaceInterface.getReferenceTypes(data.rootWorkspace),
    data.type
  );
};

const getLineBeginning: GetLineBeginning = (referenceId, isAggregated) => {
  const data = get(referenceId);
  if (!data) return ArrowType.NONE;
  return modelOperations.getLineBeginning(
    resolveModelType(data),
    data.sourceCardinality,
    isAggregated
  );
};

const getLineEnding: GetLineEnding = (referenceId, isAggregated) => {
  const data = get(referenceId);
  if (!data) return ArrowType.NONE;
  return modelOperations.getLineEnding(
    resolveModelType(data),
    data.targetCardinality,
    isAggregated
  );
};

const getReferenceTypeName: GetReferenceTypeName = ({
  model,
  type,
  rootWorkspace,
}: APIReferenceAttributes) => {
  if (model === VIRTUAL_MODEL_ID) {
    return getVirtualReferenceType(type)?.name ?? null;
  }
  const existingRefTypes =
    workspaceInterface.getReferenceTypes(rootWorkspace) || [];
  return modelOperations.getRefTypeName(existingRefTypes, type);
};

const getReferenceTypeNameByReferenceId: GetReferenceTypeNameByReferenceId =
  referenceId => {
    const data = get(referenceId);
    if (!data) return null;
    return getReferenceTypeName(data);
  };

const getStyle: GetStyle = referenceId => {
  const data = get(referenceId);
  if (!data) return null;
  const refType =
    data.model === VIRTUAL_MODEL_ID
      ? getVirtualReferenceType(data.type)
      : modelOperations.getRefType(
          workspaceInterface.getReferenceTypes(data.rootWorkspace),
          data.type
        );
  return refType ? modelOperations.getStyle(refType) : null;
};

const getFields: GetFields = referenceId => {
  const data = get(referenceId);
  if (!data) return [];
  return fieldInterface.getFieldsByReferenceType(data.type, data.model);
};

const getManagedStatesForReference = (referenceId: ArdoqId) => {
  const data = get(referenceId);
  if (!data) return [];
  const jobIds = workspaceInterface
    .getWorkspaceIntegrations(data.rootWorkspace)
    .map(x => x._id);
  const managedData = data['ardoq-persistent']?.managed || {};
  return jobIds.filter(x => managedData[x]).map(x => managedData[x]);
};

const isExternallyManaged: IsExternallyManaged = referenceId => {
  const activeManagedDefinitions = getManagedStatesForReference(referenceId);
  return Boolean(activeManagedDefinitions.find(x => x.seenInLastImport));
};

const getModelType: GetModelType = referenceId => {
  const data = get(referenceId);
  if (!data) return null;
  return resolveModelType(data);
};

const getGlobalReferenceType: GetGlobalReferenceType = referenceId => {
  const data = get(referenceId);
  if (!data || isNil(data.type)) return null;

  const referenceTypeName = getReferenceTypeName(data);
  if (!referenceTypeName) return null;

  return {
    id: referenceOperations.getGlobalTypeId(data),
    name: referenceTypeName,
  };
};

const getTypeNameFilter: GetTypeNameFilter = (table, virtualTypeId) => id => {
  const data = get(id);
  if (!data) return false;
  const { rootWorkspace, type, model } = data;
  if (model === VIRTUAL_MODEL_ID) {
    return type === virtualTypeId;
  }
  return Boolean(rootWorkspace && table[rootWorkspace] === type);
};

const getWorkspaceReferences = (rootWorkspace: ArdoqId) =>
  getAllReferences()
    .filter(ref => ref.rootWorkspace === rootWorkspace)
    .map(ref => structuredClone(ref));

const getReferencesCount: GetReferencesCount = () => getAllReferences().length;

const getStubReferencesWithTypeName: GetStubReferencesWithTypeName = () =>
  getAllReferences().map(ref => {
    const referenceTypes = workspaceInterface.getReferenceTypes(
      ref.rootWorkspace
    );
    const refType = modelOperations.getRefType(referenceTypes, ref.type);
    return {
      ...pick(ref, ['_id', 'displayText', 'source', 'target']),
      typeName: refType ? refType.name : '',
    };
  });

const getGraphSnapshot: GetGraphSnapshot = (
  scopeReferenceIds,
  parentChildReferences
) => {
  const allRefs = getAllReferences();
  const visibleReferenceIds = new Set(scopeReferenceIds);
  const referencesInTheGraph = allRefs.filter(ref =>
    visibleReferenceIds.has(ref._id)
  );
  const sourceMap = new Map<ArdoqId, LinkedComponent[]>();
  const targetMap = new Map<ArdoqId, LinkedComponent[]>();
  const referenceMap = new Map<ArdoqId, SimpleReferenceModel>();
  const addToMaps = getAddToMaps({ referenceMap, sourceMap, targetMap });
  referencesInTheGraph.forEach(
    ({ _id: referenceId, source: sourceId, target: targetId }) =>
      addToMaps({ referenceId, sourceId, targetId })
  );
  parentChildReferences.forEach(({ source, target, _id }) =>
    addToMaps({ referenceId: _id, sourceId: source, targetId: target })
  );
  return {
    ...getEmptyComponentGraphData(),
    sourceMap,
    targetMap,
    referenceMap,
    referenceTypes: [],
  };
};

const getAllReferences = (): APIReferenceAttributes[] =>
  references$.state.models;

const getMatchingColorFilterIds = (referenceId: ArdoqId): string[] => {
  const reference = get(referenceId);
  if (!reference) return [];
  const fields = fieldInterface.getFieldsByReferenceType(
    reference.type,
    reference.model
  );
  const referenceAdjacency = getCurrentGraphModel();
  const referenceTypeName = getReferenceTypeName(reference);
  return Filters.getFormattingFilters()
    .filter(
      filter =>
        filter.get('color') &&
        filter.isAffected(APIEntityType.REFERENCE) &&
        filter.isIncluded(
          APIEntityType.REFERENCE,
          reference,
          fields,
          referenceAdjacency,
          referenceTypeName
        )
    )
    .map(({ cid }) => cid);
};

/**
 * `referenceInterface` is a Backbone-era singleton: every member resolves the
 * reference, and often its model type and the active filters, from global mutable
 * state at call time.
 *
 * The app is stream-based now. Resolve references from `references$`
 * (`streams/references/references$.ts`) at a permitted boundary -- a `*$.ts`
 * file, a file inside a `*$/` folder, or a `*routines.ts` -- and thread the
 * resolved data downstream as plain parameters.
 *
 * Reference *type* resolution has pure counterparts in `modelOperations`
 * (`@ardoq/core`) -- `getRefType`, `getRefTypeById`, `getRefTypeName`,
 * `getModelType`, `getStyle`, `getLineBeginning`, `getLineEnding`. Label
 * formatting lives in `referenceLabelOperations`
 * (`@ardoq/reference-operations`), attribute shaping in
 * `referenceAttributesOperations` (`@ardoq/core`), and the `EnhancedScopeData`
 * flavour -- including documented pure replacements for `getCssClassNames` and
 * `getCssColors` -- in `referenceUtils` (`@ardoq/scope-data`).
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe and the interface-to-stream substitution table.
 */
export const referenceInterface: ReferenceInterfaceImplementation = {
  /**
   * @deprecated Call `referenceUtils.toGlobalReferenceTypeId` or `referenceUtils.getGlobalTypeId` (`@ardoq/scope-data`).
   */
  getGlobalTypeId,
  /**
   * @deprecated Join `includedInContext$` (`streams/filters/includedInContext$.ts`), or call the pure `isReferenceIdIncludedInContextByFilter` from `@ardoq/reference-interface` with resolved filters.
   */
  isIncludedInContextByFilter,
  /**
   * @deprecated Call `referenceUtils.getReferenceDisplayColorAsSVGAttributes(scopeData, filterColorData, referenceId, options)` (`@ardoq/scope-data`).
   */
  getReferenceCssColors,
  /**
   * @deprecated Call `referenceLabelOperations` (`@ardoq/reference-operations`) with the label rules from `formattingAndLabelRules$` and fields from `fields$`.
   */
  getDisplayLabel,
  /**
   * @deprecated Read `references$.byId[id]` for presence, or call `referenceUtils.isReference` (`@ardoq/scope-data`).
   */
  isReference,
  /**
   * @deprecated Call `fieldOperations.isURLField` (`@ardoq/core`) over fields from `fields$`.
   */
  hasURLFields,
  /**
   * @deprecated Call `referenceUtils.getReferenceCSSClassNames(scopeData, filterColorData, referenceId)` (`@ardoq/scope-data`) -- documented as the pure replacement for this member.
   */
  getCssClassNames,
  /**
   * @deprecated Call `referenceUtils.getReferenceDisplayColorAsSVGAttributes(scopeData, filterColorData, referenceId, options)` (`@ardoq/scope-data`) -- documented as the pure replacement for this member.
   */
  getCssColors,
  /**
   * @deprecated Read `references$.models` and filter on `source` / `target`, or join `graphModel$` for the adjacency.
   */
  getReferencesForComponent,
  /**
   * @deprecated Read `references$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getReferencesInWorkspace,
  /**
   * @deprecated Read `references$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getReferencesInRootWorkspace,
  /**
   * @deprecated Call `modelOperations.getLineBeginning(referenceType)` (`@ardoq/core`).
   */
  getLineBeginning,
  /**
   * @deprecated Call `modelOperations.getLineEnding(referenceType)` (`@ardoq/core`).
   */
  getLineEnding,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getReferenceTypeByReferenceId` (`@ardoq/scope-data`), or `modelOperations.getRefTypeName(referenceTypes, typeId)` (`@ardoq/core`).
   */
  getReferenceTypeNameByReferenceId,
  /**
   * @deprecated Call `modelOperations.getStyle(referenceType)` (`@ardoq/core`).
   */
  getStyle,
  /**
   * @deprecated Call `enhancedScopeDataOperations.getReferenceFields` (`@ardoq/scope-data`), or select from `fields$` with `fieldOperations` (`@ardoq/core`).
   */
  getFields,
  /**
   * @deprecated Read `references$.byId[id]` and inspect its own `ardoq-persistent` managed payload against the workspace integrations from `workspaceUtils.getWorkspaceIntegrations` (`@ardoq/scope-data`).
   */
  isExternallyManaged,
  /**
   * @deprecated Call `modelOperations.getModelType(referenceTypes, typeId)` (`@ardoq/core`) with types from `models$`.
   */
  getModelType,
  /**
   * @deprecated Read `references$.byId[id]`.
   */
  get,
  /**
   * @deprecated Call `modelOperations.getRefType(referenceTypes, typeId)` (`@ardoq/core`) with types from `models$`.
   */
  getGlobalReferenceType,
  /**
   * @deprecated Call `modelOperations.getRefTypeName` (`@ardoq/core`) over types from `models$` and build the predicate from the result.
   */
  getTypeNameFilter,
  /**
   * @deprecated Read `references$.models` and filter on `rootWorkspace`; `entityOperations.toIds` (`@ardoq/core`) if you only need the ids.
   */
  getWorkspaceReferences,
  /**
   * @deprecated Read `references$.models.length`.
   */
  getReferencesCount,
  /**
   * @deprecated Join `references$` and `models$` and shape the stubs with `modelOperations.getRefTypeName` (`@ardoq/core`).
   */
  getStubReferencesWithTypeName,
  /**
   * @deprecated Join `graphModel$` (`modelInterface/graphModel$.ts`), or call `enhancedScopeDataOperations.getGraphSnapshot` (`@ardoq/scope-data`).
   */
  getGraphSnapshot,
  /**
   * @deprecated Read `references$.models`.
   */
  getAllReferences,
  /**
   * @deprecated Call `filterColorDataOperations.getReferenceFilterColor` (`@ardoq/scope-data`) with the conditional formatting resolved from `streams/filters/`.
   */
  getMatchingColorFilterIds,
};
